import { useCallback, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
} from '@antimatterhq/ui/components/card';
import { Input } from '@antimatterhq/ui/components/input';
import { Button } from '@antimatterhq/ui/components/button';
import { Text } from '@antimatterhq/ui/components/typography';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Icon } from '@antimatterhq/ui/components/icon';
import { useColorMode } from '@antimatterhq/ui/contexts/ColorModeContext';
import { useInstanceId } from '@/contexts/InstanceIdContext';
import { useStandaloneAuthentication } from '@/hooks/authentication/useStandaloneAuthentication';
import { extractDatabricksInstanceId } from '@/utils/extractDatabricksInstanceId';

const ErrorMessage = ({ children }: { children: React.ReactNode }) => {
  return (
    <span className="flex items-center gap-1 text-red-600 dark:text-red-500">
      <Icon
        icon={'lucide:octagon-x'}
        height="1.2rem"
        width="1.2rem"
        className="shrink-0"
      />
      <Text color="error">{children}</Text>
    </span>
  );
};

export default function Login() {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { storeInstanceId } = useInstanceId();
  const { initiateAuthentication } = useStandaloneAuthentication();
  const { isDarkMode } = useColorMode();

  const handleLogin = useCallback(() => {
    const instanceId = extractDatabricksInstanceId(url);
    if (!instanceId) {
      setError('Invalid URL');
      return;
    }

    // Don't set the instance ID in the context, we want to wait for the login to complete
    storeInstanceId(instanceId, false);
    initiateAuthentication(instanceId);
  }, [url, storeInstanceId, initiateAuthentication]);

  const handleUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUrl(e.target.value);
      setError(null);
    },
    [],
  );

  return (
    <main className="flex h-screen w-screen flex-col items-center justify-center gap-4 p-4">
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <form
          className="flex w-[440px] flex-col items-center justify-center gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleLogin();
          }}
        >
          <div className="flex items-center justify-center gap-2 p-2">
            <img
              src={
                isDarkMode
                  ? '/images/logo/antimatter-full-logo-dark.svg'
                  : '/images/logo/antimatter-full-logo-light.svg'
              }
              alt="avatar"
              className="h-8 w-full object-contain"
            />
          </div>
          <Card className="w-full">
            <CardHeader>
              <Text size="lg" className="font-bold">
                Please enter a Databricks workspace URL
              </Text>
            </CardHeader>
            <CardContent>
              <Input
                type="text"
                data-testid="workspace-url-input"
                placeholder="Workspace URL"
                onChange={handleUrlChange}
                value={url}
              />
              <Spacer size="lg" />
              <Button
                type="submit"
                className="w-full"
                variant="primary"
                data-testid="login-button"
              >
                Go
              </Button>
              <Spacer size="base" />
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </CardContent>
          </Card>
        </form>
      </div>
    </main>
  );
}
